import type { DriverAdapter } from "@prisma/driver-adapter-utils";
import type { Generator } from "@prisma/internals";
import type { ConnectorType, IDMMF } from "@prisma/studio-common";
import * as crypto from "crypto"; // TODO: only take what we need!
import { stat } from "fs/promises";
import { dirname } from "path";
import type { PreviewFeatures } from "./dmmf";
import type { PrismaClientPath, PrismaEnginePath } from "./paths";
import { dmmfToRuntimeDataModel } from "./runtimeDataModel";
import { debug } from "./utils/debug";
import type { PrismaEngineType } from "./utils/prismaEngineName";

type GetPrismaClientOptions = {
  schema: string;
  schemaPath: string;
  dmmf: IDMMF;
  adapter?: DriverAdapter;
  datasourceProvider: ConnectorType;
  previewFeatures: PreviewFeatures;
  engineType: PrismaEngineType;
  /**
   * Data sources to pass to the Prisma Client constructor.
   */
  datasources?: Record<string, { url?: string }>;
  paths: {
    queryEngine?: PrismaEnginePath;
    prismaClient: PrismaClientPath;
  };
  directUrl?: string;
  versions?: {
    prisma?: string;
    queryEngine?: string;
  };
};

/**
 * @see https://regex101.com/r/43HCLs/1
 */
const datasourceNamePattern = /^\s*datasource\s+([^\s]+)\s*{/m;
const datasourceEnvUrlPattern = /url *= *env\("(.*)"\)/;
const datasourceRawUrlPattern = /url *= *"(.*)"/;

declare global {
  const STUDIO_EMBED_BUILD: boolean | undefined;
}

/**
 * Get an in-memory instance of Prisma Client
 */
export async function getPrismaClient({
  schema,
  schemaPath,
  dmmf,
  adapter,
  datasourceProvider,
  previewFeatures,
  datasources,
  engineType,
  paths,
  directUrl,
  versions,
}: GetPrismaClientOptions): Promise<{
  prisma: any;
  PrismaClient: any;
  PrismaClientKnownRequestError: any;
  PrismaClientRustPanicError: any;
  PrismaClientInitializationError: any;
  PrismaClientValidationError: any;
}> {
  const datasourceName = schema.match(datasourceNamePattern)?.[1] ?? "";
  const datasourceEnvUrl = schema.match(datasourceEnvUrlPattern)?.[1] ?? null;
  const datasourceRawUrl = schema.match(datasourceRawUrlPattern)?.[1] ?? null;
  const {
    getPrismaClient: InternalsGetPrismaClient,
    PrismaClientKnownRequestError,
    PrismaClientRustPanicError,
    PrismaClientInitializationError,
    PrismaClientValidationError,
  } = require(typeof STUDIO_EMBED_BUILD !== "undefined" && STUDIO_EMBED_BUILD
    ? "@prisma/client/runtime/edge.js"
    : `${paths.prismaClient}/runtime/${engineType}`);

  const inlineSchema = schema;
  const inlineSchemaHash = crypto
    .createHash("sha256")
    .update(Buffer.from(inlineSchema, "utf8").toString("base64"))
    .digest("hex");

  // Get PrismaClient constructor
  const PrismaClient = InternalsGetPrismaClient({
    runtimeDataModel: dmmfToRuntimeDataModel(dmmf.datamodel),
    generator: {
      name: "studio-client",
      provider: { value: "prisma-client-js", fromEnvVar: null },
      output: null,
      binaryTargets: [],
      previewFeatures,
      config: {},
    },
    clientVersion: versions?.prisma ?? "in-memory",
    engineVersion: versions?.queryEngine ?? "in-memory",
    // Exists because this is how QE resolves schema location. (Prisma Client uses PRISMA_DML_PATH, and not PRISMA_DML)
    // If a schema path folder is passed, we need to pass the folder, not its parent directory
    dirname: (await stat(schemaPath)).isDirectory()
      ? schemaPath
      : dirname(schemaPath),
    activeProvider: datasourceProvider,
    datasourceNames: [datasourceName],
    // SQLite-specific options follow
    relativePath: "",
    relativeEnvPaths: {
      rootEnvPath: "",
      schemaEnvPath: "",
    },
    inlineDatasources: {
      [datasourceName]: {
        url: {
          fromEnvVar: datasourceEnvUrl,
          value: datasourceRawUrl,
        },
      },
    },
    inlineSchema,
    inlineSchemaHash,
  });

  if (directUrl) {
    datasources = { [datasourceName]: { url: directUrl } };
  }

  debug("[getPrismaClient]", {
    prismaClientPath: paths.prismaClient,
    queryEngine: paths.queryEngine,
    previewFeatures,
    datasources,
  });

  // Construct PrismaClient
  const prisma = new PrismaClient({
    errorFormat: "colorless",
    adapter: adapter || null,
    datasources,
    __internal: {
      engine: {
        binaryPath: paths.queryEngine?.path,
      },
    },
  });

  return {
    prisma,
    PrismaClient,
    PrismaClientKnownRequestError,
    PrismaClientRustPanicError,
    PrismaClientInitializationError,
    PrismaClientValidationError,
  };
}

type GetPrismaClientTypesOptions = {
  generator: Generator;
};
